import type { BaseRecord, DataProvider } from "@refinedev/core";
import { apiBaseUrl, apiRequest } from "./api-client";

const resourcePaths: Record<string, string> = {
  bots: "/trading/bots",
  strategies: "/trading/strategies",
  orders: "/trading/orders",
  positions: "/trading/positions",
  backtests: "/backtests",
  brokers: "/trading/brokers",
  brokerConnections: "/trading/broker-connections",
  brokerAccounts: "/trading/broker-accounts",
  auditLogs: "/trading/audit-logs",
  dataQuality: "/trading/data-quality",
  alerts: "/trading/alerts",
};

function pathFor(resource: string, id?: string | number): string {
  const base = resourcePaths[resource];
  if (!base) throw new Error(`Unsupported API resource: ${resource}`);
  return id === undefined ? base : `${base}/${encodeURIComponent(String(id))}`;
}

function applyFilters(data: BaseRecord[], filters: any[] = []): BaseRecord[] {
  return data.filter((record) => filters.every((filter) => {
    if (!filter || !("field" in filter)) return true;
    const value = (record as Record<string, unknown>)[filter.field];
    const expected = filter.value;
    if (expected === undefined || expected === null || expected === "") return true;
    if (filter.operator === "contains") return String(value ?? "").toLowerCase().includes(String(expected).toLowerCase());
    if (filter.operator === "in" && Array.isArray(expected)) return expected.includes(value);
    return value === expected;
  }));
}

function applySort(data: BaseRecord[], sorters: any[] = []): BaseRecord[] {
  const copy = [...data];
  for (const sorter of [...sorters].reverse()) {
    copy.sort((a, b) => {
      const av = (a as Record<string, unknown>)[sorter.field];
      const bv = (b as Record<string, unknown>)[sorter.field];
      const order = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true });
      return sorter.order === "desc" ? -order : order;
    });
  }
  return copy;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function mapBacktest(row: Record<string, any>): BaseRecord {
  const summary = row.summary ?? {};
  const initial = asNumber(row.initialEquity);
  const ending = asNumber(row.endingEquity, initial);
  const strategy = row.profile ?? "xau-session-momentum-v1";
  const name = ({
    "xau-session-momentum-v1": "XAU Session Momentum",
    "xau-regime-consensus-session-v1": "XAU Regime Consensus Session",
    "xau-multi-edge-portfolio-v1": "XAU Multi-Edge Portfolio",
    "xau-near-miss-range-v1": "XAU Near-Miss Range",
    "xau-multi-edge-3-v1": "XAU Multi-Edge 3",
  } as Record<string, string>)[strategy] ?? strategy;
  return {
    ...row,
    id: row.id,
    name,
    strategy,
    symbol: String(row.costModel?.instrument ?? "XAUUSD").replace(/^SPOT_/, ""),
    period: `${row.reportStart ?? "?"} – ${row.reportEnd ?? "?"}`,
    status: "COMPLETED",
    returnPct: initial > 0 ? (ending / initial - 1) * 100 : 0,
    cagr: asNumber(row.completedYearsCagr) * 100,
    maxDrawdown: Math.abs(asNumber(row.reportMtmMaxDrawdown ?? row.maxDrawdown)) * 100,
    trades: asNumber(summary.executedOrderEvents),
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : "",
  };
}

function mapStrategy(row: Record<string, any>): BaseRecord {
  return {
    ...row,
    id: row.id,
    name: String(row.id).split("-").map((part: string) => part.charAt(0).toUpperCase() + part.slice(1)).join(" "),
    version: "backend registry",
    market: Array.isArray(row.supportedMarkets) ? row.supportedMarkets.join(", ") : "",
  };
}

function mapResource(resource: string, value: unknown): BaseRecord[] {
  const rows = Array.isArray(value) ? value : [];
  if (resource === "backtests") return rows.map((row) => mapBacktest(row));
  if (resource === "strategies") return rows.map((row) => mapStrategy(row));
  return rows as BaseRecord[];
}

async function refreshBot(id: string | number): Promise<BaseRecord> {
  return apiRequest<BaseRecord>(pathFor("bots", id));
}

export const dataProvider: DataProvider = {
  getApiUrl: () => apiBaseUrl,
  getList: async ({ resource, pagination, filters, sorters }) => {
    const path = resource === "auditLogs" ? `${pathFor(resource)}?limit=5000` : pathFor(resource);
    let data = mapResource(resource, await apiRequest<unknown>(path));
    data = applySort(applyFilters(data, filters as any[]), sorters as any[]);
    const total = data.length;
    const current = pagination?.currentPage ?? 1;
    const pageSize = pagination?.pageSize ?? (total || 10);
    const start = (current - 1) * pageSize;
    return { data: data.slice(start, start + pageSize) as any[], total };
  },
  getOne: async ({ resource, id }) => {
    const raw = await apiRequest<unknown>(pathFor(resource, id));
    const record = resource === "backtests" ? mapBacktest(raw as Record<string, any>) : raw as BaseRecord;
    if (!record) throw new Error(`Record ${resource}/${String(id)} not found`);
    return { data: record as any };
  },
  create: async ({ resource, variables }) => {
    if (resource === "bots") {
      const data = await apiRequest<BaseRecord>(pathFor(resource), { method: "POST", body: JSON.stringify(variables) });
      return { data: data as any };
    }
    if (resource === "backtests") {
      const raw = await apiRequest<Record<string, any>>("/backtests/run", { method: "POST", body: JSON.stringify(variables) });
      const saved = await apiRequest<Record<string, any>>(`/backtests/${encodeURIComponent(String(raw.runId))}`);
      return { data: mapBacktest(saved) as any };
    }
    throw new Error(`Create is not supported for ${resource}`);
  },
  update: async ({ resource, id, variables }) => {
    if (resource !== "bots") throw new Error(`Update is not supported for ${resource}`);
    const values = variables as Record<string, any>;
    if (values.strategyConfig || values.safety || values.brokerAccountId) {
      await apiRequest(pathFor("bots", id) + "/settings", {
        method: "PATCH",
        body: JSON.stringify({ strategyConfig: values.strategyConfig, safety: values.safety, brokerAccountId: values.brokerAccountId }),
      });
    }
    const status = String(values.status ?? "").toUpperCase();
    if (status === "RUNNING") {
      const current = await refreshBot(id) as Record<string, unknown>;
      await apiRequest(pathFor("bots", id) + (String(current.status).toUpperCase() === "PAUSED" ? "/resume" : "/start"), { method: "POST" });
    } else if (status === "PAUSED") {
      await apiRequest(pathFor("bots", id) + "/pause", { method: "POST", body: JSON.stringify({ reason: "UI_OPERATOR_PAUSE" }) });
    } else if (status === "STOPPED") {
      await apiRequest(pathFor("bots", id) + "/stop", { method: "POST" });
    } else if (values.enabled === false) {
      await apiRequest(pathFor("bots", id) + "/disable", { method: "POST" });
    } else if (values.enabled === true) {
      await apiRequest(pathFor("bots", id) + "/enable", { method: "POST" });
    }
    return { data: await refreshBot(id) as any };
  },
  deleteOne: async ({ resource, id }) => {
    if (resource !== "bots" && resource !== "backtests") throw new Error(`Delete is not supported for ${resource}`);
    await apiRequest(pathFor(resource, id), { method: "DELETE" });
    return { data: { id } as any };
  },
  custom: async ({ url, method, payload }) => {
    const customPaths: Record<string, string> = {
      "/system/health": "/trading/system/health",
      "/portfolio/summary": "/trading/portfolio/summary",
      "/positions/flatten-all": "/trading/positions/flatten-all",
    };
    const path = customPaths[url] ?? (url.startsWith("/trading/") || url.startsWith("/backtests") ? url : `/trading${url}`);
    const data = await apiRequest(path, {
      method: method.toUpperCase(),
      body: method.toLowerCase() === "get" ? undefined : JSON.stringify(payload ?? {}),
    });
    return { data: data as any };
  },
};
